// ===========================================
// PIXI REGISTRY - SPRITE CREATION
// ===========================================
// Shared sprite creation functions for cards,
// chips, and kitty displays.
// ===========================================

import { Container, Sprite, Graphics, Text, TextStyle, Texture } from 'pixi.js';
import { CHIP_SIZES, KITTY_CHIP_SIZE, KITTY_VERTICAL_OFFSET, CARD_WIDTH, CARD_HEIGHT } from './constants';

// ===========================================
// TYPES
// ===========================================

export interface CardConfig {
  scale: number;  // Percentage (100 = normal size)
}

export interface ChipCounterConfig {
  show?: boolean;
  pillOpacity?: number;
  fontColor?: string;
  fontSize?: number;
  x?: number;
  y?: number;
}

export interface ChipBorderConfig {
  color: string;
  thickness: number;
  opacity: number;
}

export interface KittyChipData {
  sprite: Sprite;
  border: Graphics;
  count: number;
}

export interface KittyDisplay {
  container: Container;
  borderContainer: Container;
  chipContainer: Container;
  pill: Graphics;
  counterText: Text;
}

// ===========================================
// KITTY DISPLAY CREATION
// ===========================================

/** Default kitty counter config */
const DEFAULT_COUNTER_CONFIG: ChipCounterConfig = {
  show: false,
  pillOpacity: 80,
  fontColor: '#FB923C',
  fontSize: 12,
  x: 0,
  y: 0,
};

/** Counter position offset below chips (matches KITTY_CONFIG.counterOffsetY in PixiGameTableV1) */
const COUNTER_OFFSET_Y = 10;

/**
 * Create a kitty display container with chip and counter infrastructure.
 * The container is ready to receive chips via addChipToKitty().
 *
 * @param counterConfig - Optional counter styling configuration
 * @returns Kitty display components
 */
export function createKittyDisplay(counterConfig?: ChipCounterConfig): KittyDisplay {
  const config = { ...DEFAULT_COUNTER_CONFIG, ...counterConfig };

  const container = new Container();
  container.sortableChildren = true;
  container.visible = true;
  container.label = 'kittyContainer'; // Label to prevent orphan cleanup from destroying it

  // Border container (holds circular borders behind chips)
  const borderContainer = new Container();
  borderContainer.sortableChildren = true;
  borderContainer.zIndex = 5;
  container.addChild(borderContainer);

  // Chip container (holds the actual chip sprites)
  const chipContainer = new Container();
  chipContainer.sortableChildren = true;
  chipContainer.zIndex = 10;
  container.addChild(chipContainer);

  // Counter pill background
  const pill = new Graphics();
  pill.zIndex = 50;
  container.addChild(pill);

  // Counter text
  const counterStyle = new TextStyle({
    fontFamily: 'Arial, sans-serif',
    fontSize: config.fontSize,
    fontWeight: 'bold',
    fill: config.fontColor,
  });
  const counterText = new Text({ text: '', style: counterStyle });
  counterText.anchor.set(0.5, 0.5);
  counterText.zIndex = 51;
  counterText.visible = false;
  container.addChild(counterText);

  return { container, borderContainer, chipContainer, pill, counterText };
}

/**
 * Update the kitty counter layout based on chip count and total.
 *
 * @param pill - Graphics object for pill background
 * @param counterText - Text object for counter
 * @param chipCount - Number of chip denominations displayed
 * @param total - Total bet amount
 * @param counterOffset - Optional x,y offset for counter position
 */
export function updateKittyLayout(
  pill: Graphics,
  counterText: Text,
  chipCount: number,
  total: number,
  counterOffset: { x: number; y: number } = { x: 0, y: 0 }
): void {
  const counterX = counterOffset.x;
  const counterY = COUNTER_OFFSET_Y + counterOffset.y;

  if (chipCount > 0) {
    counterText.visible = true;
    counterText.text = total.toLocaleString();

    // Measure text width to size pill dynamically
    const textWidth = counterText.width;
    const pillPadding = { x: 5, y: 3 };
    const pillWidth = Math.max(KITTY_CHIP_SIZE, textWidth) + pillPadding.x * 2;
    const pillHeight = (counterText.style.fontSize as number) + pillPadding.y * 2;

    // Redraw pill sized to text
    pill.clear();
    pill.roundRect(-pillWidth / 2 + counterX, counterY - pillPadding.y, pillWidth, pillHeight, 8);
    pill.fill({ color: 0x000000, alpha: 0.8 });

    // Center text within pill
    counterText.x = counterX;
    counterText.y = counterY - pillPadding.y + pillHeight / 2;
  } else {
    counterText.visible = false;
    pill.clear();
  }
}

// ===========================================
// KITTY CHIP MANAGEMENT
// ===========================================

/**
 * Add a chip sprite to a kitty display.
 * Handles denomination-based stacking (one visual chip per denomination).
 *
 * @param chipContainer - Container for chip sprites
 * @param borderContainer - Container for chip borders
 * @param denomination - Chip denomination
 * @param chipSprite - The chip sprite to add
 * @param kittyChipMap - Map tracking chips by denomination
 * @param borderConfig - Border styling configuration
 * @returns True if chip was added, false if denomination already exists (count incremented)
 */
export function addChipToKitty(
  chipContainer: Container,
  borderContainer: Container,
  denomination: number,
  chipSprite: Sprite,
  kittyChipMap: Map<number, KittyChipData>,
  borderConfig: ChipBorderConfig
): boolean {
  // Check if this denomination already exists in the kitty
  const existingData = kittyChipMap.get(denomination);
  if (existingData) {
    existingData.count++;
    chipSprite.destroy();
    return false;
  }

  // New denomination - configure sprite
  (chipSprite as unknown as { denom: number }).denom = denomination;
  chipSprite.width = KITTY_CHIP_SIZE;
  chipSprite.height = KITTY_CHIP_SIZE;
  chipSprite.anchor.set(0.5);
  chipSprite.x = 0;
  chipSprite.rotation = Math.random() * 2 * Math.PI;
  chipContainer.addChild(chipSprite);

  // Create circular border for this chip
  const border = new Graphics();
  const borderColor = parseInt(borderConfig.color.replace('#', ''), 16);
  border.circle(0, 0, KITTY_CHIP_SIZE / 2);
  border.stroke({
    color: borderColor,
    width: borderConfig.thickness,
    alpha: borderConfig.opacity / 100,
  });
  border.x = 0;
  borderContainer.addChild(border);

  // Track in map
  kittyChipMap.set(denomination, { sprite: chipSprite, border, count: 1 });

  // Reposition all chips based on sorted denomination order
  repositionKittyChips(chipContainer, borderContainer, kittyChipMap);

  return true;
}

/**
 * Reposition all chips and borders in a kitty based on sorted denomination order.
 * Bottom-justified: smallest at bottom (y=0), largest at top (negative Y).
 *
 * @param chipContainer - Container for chip sprites
 * @param borderContainer - Container for chip borders
 * @param kittyChipMap - Map tracking chips by denomination
 */
export function repositionKittyChips(
  chipContainer: Container,
  borderContainer: Container,
  kittyChipMap: Map<number, KittyChipData>
): void {
  // Get sorted denominations (ascending - smallest first at bottom)
  const sortedDenoms = [...kittyChipMap.keys()].sort((a, b) => a - b);

  // Position each chip and its border based on index in sorted order
  sortedDenoms.forEach((denom, index) => {
    const data = kittyChipMap.get(denom);
    if (data) {
      const yPos = -index * KITTY_VERTICAL_OFFSET;
      data.sprite.y = yPos;
      data.sprite.zIndex = 100 + index;
      data.border.y = yPos;
      data.border.zIndex = index;
    }
  });
}

/**
 * Remove a chip denomination from a kitty.
 *
 * @param denomination - Chip denomination to remove
 * @param kittyChipMap - Map tracking chips by denomination
 * @param chipContainer - Container for chip sprites
 * @param borderContainer - Container for chip borders
 * @returns True if removed, false if not found
 */
export function removeChipFromKitty(
  denomination: number,
  kittyChipMap: Map<number, KittyChipData>,
  chipContainer: Container,
  borderContainer: Container
): boolean {
  const data = kittyChipMap.get(denomination);
  if (!data) return false;

  data.count--;
  if (data.count <= 0) {
    data.sprite.destroy();
    data.border.destroy();
    kittyChipMap.delete(denomination);
    repositionKittyChips(chipContainer, borderContainer, kittyChipMap);
  }

  return true;
}

// ===========================================
// CARD SPRITE HELPERS
// ===========================================

/**
 * Calculate scaled card dimensions.
 *
 * @param scale - Scale percentage (100 = normal)
 * @returns Scaled width and height
 */
export function getScaledCardDimensions(scale: number): { width: number; height: number } {
  const scaleFactor = scale / 100;
  return {
    width: CARD_WIDTH * scaleFactor,
    height: CARD_HEIGHT * scaleFactor,
  };
}

/**
 * Get chip size for a specific context.
 *
 * @param context - Context name ('rail', 'staged', 'kittyMedium', 'kittySmall', 'dealerPile')
 * @returns Chip size in pixels
 */
export function getChipSize(context: keyof typeof CHIP_SIZES): number {
  return CHIP_SIZES[context];
}

// ===========================================
// OUTCOME LABEL CREATION
// ===========================================

export interface OutcomeLabelConfig {
  fontSize: number;
  scale: number;
  fontFamily: string;
}

const DEFAULT_OUTCOME_CONFIG: OutcomeLabelConfig = {
  fontSize: 14,
  scale: 100,
  fontFamily: 'Arial, sans-serif',
};

/**
 * Get display text and colors for an outcome.
 *
 * @param outcome - Outcome string (win, lose, push, bust, blackjack, surrender)
 * @returns Display text, background color, and text color
 */
export function getOutcomeDisplay(outcome: string): {
  text: string;
  bgColor: number;
  textColor: number;
} {
  switch (outcome.toLowerCase()) {
    case 'win':
      return { text: 'WINNER!', bgColor: 0x22c55e, textColor: 0xffffff };
    case 'blackjack':
      return { text: 'BLACKJACK!', bgColor: 0xfbbf24, textColor: 0x000000 };
    case 'lose':
      return { text: 'LOSER!', bgColor: 0xef4444, textColor: 0xffffff };
    case 'bust':
      return { text: 'BUSTED!', bgColor: 0xef4444, textColor: 0xffffff };
    case 'push':
      return { text: 'PUSH', bgColor: 0x6b7280, textColor: 0xffffff };
    case 'surrender':
      return { text: 'SURRENDER', bgColor: 0x9ca3af, textColor: 0xffffff };
    default:
      return { text: outcome.toUpperCase(), bgColor: 0x1f2937, textColor: 0xffffff };
  }
}

/**
 * Create an outcome label container.
 *
 * @param outcome - Outcome string
 * @param config - Optional styling configuration
 * @returns Container with styled outcome label
 */
export function createOutcomeLabel(
  outcome: string,
  config: OutcomeLabelConfig = DEFAULT_OUTCOME_CONFIG
): Container {
  const container = new Container();
  const { text: displayText, bgColor, textColor } = getOutcomeDisplay(outcome);

  if (!displayText) return container;

  const scale = config.scale / 100;
  const padding = 6 * scale;

  // Create text
  const textStyle = new TextStyle({
    fontFamily: config.fontFamily,
    fontSize: config.fontSize,
    fontWeight: 'bold',
    fill: textColor,
  });
  const text = new Text({ text: displayText, style: textStyle });

  // Create background
  const leftPadding = 3;
  const boxWidth = leftPadding + text.width + padding;
  const boxHeight = text.height + padding;

  const bg = new Graphics();
  const radius = 4 * scale;

  // Custom shape: only top-right corner rounded
  bg.moveTo(0, 0);
  bg.lineTo(boxWidth - radius, 0);
  bg.arcTo(boxWidth, 0, boxWidth, radius, radius);
  bg.lineTo(boxWidth, boxHeight);
  bg.lineTo(0, boxHeight);
  bg.lineTo(0, 0);
  bg.fill({ color: bgColor });
  container.addChild(bg);

  text.x = leftPadding;
  text.y = padding / 2;
  container.addChild(text);

  container.scale.set(scale);

  return container;
}
